import { useCallback, useEffect, useMemo, useState } from 'react';
import { ALL_EQUIPMENT_CLASSES, isReservableClass } from '../constants/boatClasses';
import { getAuthHeaders } from '../utils/api';
import {
  DEFAULT_RESERVATION_END_TIME,
  DEFAULT_RESERVATION_START_TIME,
  RESERVATION_TIME_OPTIONS,
  formatReservationTimeLabel,
  getLocalISODate,
  reservationWindowsOverlap
} from '../utils/reservationTimes';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

function formatDateMMDDYYYY(isoDate) {
  const [year, month, day] = (isoDate || '').split('-');
  if (!year || !month || !day) return '';
  return `${month}/${day}/${year}`;
}

export default function MassBoatReservation({
  token,
  boats = [],
  canReserveBoat,
  onCreated
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [reservationDate, setReservationDate] = useState(getLocalISODate());
  const [startTime, setStartTime] = useState(DEFAULT_RESERVATION_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_RESERVATION_END_TIME);
  const [purpose, setPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [dayReservations, setDayReservations] = useState([]);

  const reservableBoats = useMemo(
    () =>
      (boats || []).filter(
        (boat) => !boat.is_retired && isReservableClass(boat.boat_class)
      ),
    [boats]
  );

  const classOptions = useMemo(() => {
    const present = new Set(reservableBoats.map((boat) => boat.boat_class).filter(Boolean));
    return ALL_EQUIPMENT_CLASSES.filter((c) => present.has(c.value));
  }, [reservableBoats]);

  const overlapByBoatId = useMemo(() => {
    const map = new Map();
    for (const res of dayReservations) {
      if (!reservationWindowsOverlap(startTime, endTime, res.start_time, res.end_time)) continue;
      const existing = map.get(res.boat_id) || [];
      existing.push(res);
      map.set(res.boat_id, existing);
    }
    return map;
  }, [dayReservations, startTime, endTime]);

  const filteredBoats = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return reservableBoats.filter((boat) => {
      if (classFilter && boat.boat_class !== classFilter) return false;
      if (!q) return true;
      const name = String(boat.name || '').toLowerCase();
      const boatClass = String(boat.boat_class || '').toLowerCase();
      const owner = String(boat.owner_name || '').toLowerCase();
      return name.includes(q) || boatClass.includes(q) || owner.includes(q);
    });
  }, [reservableBoats, searchQuery, classFilter]);

  const boatSelectable = useCallback(
    (boat) => canReserveBoat(boat) && !overlapByBoatId.has(boat.id),
    [canReserveBoat, overlapByBoatId]
  );

  const selectableFiltered = useMemo(
    () => filteredBoats.filter(boatSelectable),
    [filteredBoats, boatSelectable]
  );

  const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);

  useEffect(() => {
    setSelectedIds((prev) => {
      const next = prev.filter((id) => {
        const boat = reservableBoats.find((b) => String(b.id) === String(id));
        if (!boat || !canReserveBoat(boat)) return false;
        return !overlapByBoatId.has(boat.id);
      });
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) return prev;
      return next;
    });
  }, [overlapByBoatId, reservableBoats, canReserveBoat]);

  useEffect(() => {
    if (!reservationDate) {
      setDayReservations([]);
      return;
    }
    let cancelled = false;
    const formattedDate = formatDateMMDDYYYY(reservationDate);
    const load = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/boats/reservations/date/${encodeURIComponent(formattedDate)}`,
          { headers: getAuthHeaders(token) }
        );
        if (!response.ok) return;
        const data = await response.json();
        if (!cancelled) setDayReservations(data.reservations || []);
      } catch (err) {
        console.error('Failed to load reservations for mass reserve:', err);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reservationDate, token]);

  const toggleBoat = (boat, checked) => {
    if (!boatSelectable(boat)) return;
    const key = String(boat.id);
    if (checked) {
      if (selectedSet.has(key)) return;
      setSelectedIds((prev) => [...prev, boat.id]);
    } else {
      setSelectedIds((prev) => prev.filter((id) => String(id) !== key));
    }
  };

  const selectVisible = () => {
    const next = new Set(selectedIds.map(String));
    selectableFiltered.forEach((boat) => next.add(String(boat.id)));
    setSelectedIds([...next].map((id) => parseInt(id, 10)));
  };

  const clearVisible = () => {
    const visible = new Set(filteredBoats.map((boat) => String(boat.id)));
    setSelectedIds((prev) => prev.filter((id) => !visible.has(String(id))));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!reservationDate || !startTime || !endTime) {
      setStatus({ type: 'error', message: 'Please choose a date and time window.' });
      return;
    }
    if (selectedIds.length === 0) {
      setStatus({ type: 'error', message: 'Select at least one boat to reserve.' });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/boats/reservations/bulk`, {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          boatIds: selectedIds,
          reservationDate: formatDateMMDDYYYY(reservationDate),
          startTime,
          endTime,
          purpose: purpose.trim()
        })
      });
      const data = await response.json();
      if (!response.ok) {
        setStatus({ type: 'error', message: data.error || data.message || 'Failed to create mass reservation' });
        return;
      }

      const createdCount = data.createdCount || 0;
      const failures = data.failures || [];
      const failedIds = new Set(failures.map((f) => String(f.boatId)));
      setSelectedIds((prev) => prev.filter((id) => failedIds.has(String(id))));

      if (createdCount > 0 && failures.length === 0) {
        setStatus({
          type: 'success',
          message: `Reserved ${createdCount} boat${createdCount === 1 ? '' : 's'} for the session window.`
        });
      } else if (createdCount > 0) {
        setStatus({
          type: 'info',
          message: `Reserved ${createdCount} boat${createdCount === 1 ? '' : 's'}. ${failures.length} could not be held.`,
          failures
        });
      } else {
        setStatus({
          type: 'error',
          message: 'No boats were reserved.',
          failures
        });
      }

      if (createdCount > 0 && typeof onCreated === 'function') {
        onCreated();
      }

      if (reservationDate) {
        const refresh = await fetch(
          `${API_URL}/api/boats/reservations/date/${encodeURIComponent(formatDateMMDDYYYY(reservationDate))}`,
          { headers: getAuthHeaders(token) }
        );
        if (refresh.ok) {
          const refreshed = await refresh.json();
          setDayReservations(refreshed.reservations || []);
        }
      }
    } catch (err) {
      console.error('Failed to create mass reservation:', err);
      setStatus({ type: 'error', message: 'Failed to create mass reservation. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-theme-primary mb-2">Mass Reserve</h2>
      <p className="text-gray-600 text-sm sm:text-base mb-4">
        Hold boats for a practice window without setting lineups. These holds do not attach a crew,
        so logging meters later will not auto-credit athletes from this reservation.
      </p>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg border shadow-sm p-4 sm:p-6 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Date</label>
            <input
              type="date"
              value={reservationDate}
              onChange={(e) => setReservationDate(e.target.value)}
              min={getLocalISODate()}
              className="border p-3 rounded w-full text-base"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Purpose (optional)</label>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="border p-3 rounded w-full text-base"
              placeholder="e.g. Saturday AM practice"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">Start Time</label>
            <select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="border p-3 rounded w-full text-base"
              required
            >
              {RESERVATION_TIME_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {formatReservationTimeLabel(t)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold mb-2">End Time</label>
            <select
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="border p-3 rounded w-full text-base"
              required
            >
              {RESERVATION_TIME_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {formatReservationTimeLabel(t)}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mb-3">
          <label className="block text-sm font-semibold mb-2">Search boats</label>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by boat name or class…"
            className="border p-3 rounded w-full text-base"
            aria-label="Search boats"
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
            className="border p-3 rounded text-base sm:w-56"
            aria-label="Filter by boat class"
          >
            <option value="">All classes</option>
            {classOptions.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={selectVisible}
              className="px-3 py-2 text-sm rounded border border-theme-primary text-theme-primary hover:bg-theme-bg-secondary"
            >
              Select visible ({selectableFiltered.length})
            </button>
            <button
              type="button"
              onClick={clearVisible}
              className="px-3 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Clear visible
            </button>
          </div>
        </div>

        <p className="text-sm text-gray-700 mb-2">
          {selectedIds.length} selected
          {searchQuery || classFilter ? ` • ${filteredBoats.length} matching search` : ''}
        </p>

        <div className="max-h-[min(420px,50vh)] overflow-y-auto border rounded p-2 space-y-1 bg-gray-50 mb-4">
          {filteredBoats.length === 0 ? (
            <p className="text-sm text-gray-500 py-3 px-2">No boats match that search.</p>
          ) : (
            filteredBoats.map((boat) => {
              const overlaps = overlapByBoatId.get(boat.id) || [];
              const canSelect = boatSelectable(boat);
              const checked = selectedSet.has(String(boat.id));
              return (
                <label
                  key={boat.id}
                  className={`flex items-start gap-3 p-2 rounded ${
                    canSelect ? 'cursor-pointer hover:bg-white' : 'opacity-70 cursor-not-allowed'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={checked}
                    disabled={!canSelect}
                    onChange={(e) => toggleBoat(boat, e.target.checked)}
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-theme-primary">{boat.name}</span>
                      <span className="text-xs text-gray-600">{boat.boat_class}</span>
                      {boat.ownership_type === 'private' && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                          Private
                        </span>
                      )}
                    </div>
                    {!canReserveBoat(boat) && (
                      <p className="text-xs text-amber-800 mt-0.5">
                        Private boat — only an owner can reserve it.
                      </p>
                    )}
                    {overlaps.length > 0 && (
                      <p className="text-xs text-red-700 mt-0.5">
                        Already reserved {formatReservationTimeLabel(overlaps[0].start_time)}–
                        {formatReservationTimeLabel(overlaps[0].end_time)}
                        {overlaps[0].user_name ? ` (${overlaps[0].user_name})` : ''}
                      </p>
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>

        {status && (
          <div
            className={`mb-4 px-3 py-3 rounded text-sm ${
              status.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : status.type === 'info'
                  ? 'bg-blue-50 border border-blue-200 text-blue-800'
                  : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            <p className="font-medium">{status.message}</p>
            {status.failures?.length > 0 && (
              <ul className="mt-2 list-disc pl-5 space-y-1">
                {status.failures.map((failure) => (
                  <li key={`${failure.boatId}-${failure.error}`}>
                    {failure.boatName || `Boat ${failure.boatId}`}: {failure.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || selectedIds.length === 0}
          className="btn-theme-primary px-4 py-3 rounded-lg w-full sm:w-auto text-base disabled:opacity-50"
        >
          {submitting
            ? 'Reserving…'
            : selectedIds.length === 0
              ? 'Reserve boats'
              : `Reserve ${selectedIds.length} boat${selectedIds.length === 1 ? '' : 's'}`}
        </button>
      </form>
    </div>
  );
}
